import { DateTime } from "luxon";
import { useContext, useMemo } from "react";

import { ACKNOWLEDGEMENT_LOOKBACK_MS } from "@storyboard/dispatch-view/acknowledgement";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { transcriptionForContent } from "@/store/feedSelectors";
import { idx, rowsByIndex, store } from "@/store/store";
import type { AccountEvent } from "@/store/types";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { DriverRow } from "@/types/driver";
import { targetCodecForBrowser } from "@/utils/audio";
import { feedFold, feedItemFold, isAcknowledgeableTmsEvent, tmsEventFold } from "./acknowledgements";
import { buildDispatchThread, type ThreadEntry, type ThreadSources, type ThreadTask } from "./dispatchThread";

/**
 * The driver's tasks inside the lookback, open and done alike, each with its pins and its acknowledgements.
 *
 * Read from the store rather than the page's fold, which holds open tasks alone: History shows a task's acknowledgement
 * too. The driver is the one a subject leads to; the workspace is the channel's.
 */
function driverTasks(feedId: string, driverMembershipId: string, now: number): ThreadTask[] {
  const workspaceId = store.get("feed", feedId)?.workspaceId;
  if (!workspaceId) return [];
  const oldest = DateTime.fromMillis(now).minus({ milliseconds: ACKNOWLEDGEMENT_LOOKBACK_MS });
  return rowsByIndex("task", idx.tasksByWorkspace, workspaceId)
    .filter((task) => DateTime.fromISO(task.createdAt) >= oldest)
    .filter((task) => task.subjects.some((subject) => subject.workspaceMembershipId === driverMembershipId))
    .map((task) => ({
      task,
      pins: rowsByIndex("taskPinBoard", idx.taskPinBoardsByTask, task.id),
      acknowledgements: rowsByIndex("taskAcknowledgement", idx.taskAcknowledgementsByTask, task.id),
    }));
}

/** Account names for the thread's receipts and events, read from the store. */
const nameForAccount: ThreadSources["nameFor"] = (accountId) => {
  if (!accountId) return null;
  const account = store.get("account", accountId);
  return account ? { name: account.name ?? null, bot: account.bot === true } : null;
};

/** A membership's name for an acknowledgement's actor: the account behind it, read from the store. */
export function nameForMembership(membershipId: string | null | undefined): string | null {
  if (!membershipId) return null;
  const membership = store.get("workspaceMembership", membershipId);
  return nameForAccount(membership?.accountId)?.name ?? null;
}

/** The collections the thread reads, for `useCollectionsCompute`. */
export const threadCollections = [
  "item",
  "transcription",
  "audioEncoding",
  "file",
  "link",
  "accountEvent",
  "account",
  "workspaceMembership",
  "tmsEvent",
  "feed",
  "feedAcknowledgement",
  "feedItemAcknowledgement",
  "task",
  "taskPinBoard",
  "taskAcknowledgement",
] as const;

const NO_ENTRIES: ThreadEntry[] = [];

/** The driver whose channel the thread shows: the account whose receipts count, the membership whose events show. */
export type ThreadDriver = Pick<DriverRow, "accountId" | "workspaceMembershipId">;

/** A board's label by view id, which History names a task's board by; null while the workspace shows no tasks. */
export type ViewLabelFor = ((viewId: string) => string | null) | null;

/** No board has a label: what the thread reads while it shows no tasks. */
const NO_VIEW_LABEL = (): string | null => null;

/**
 * The store-backed sources the pure thread builder reads, for the feed named.
 *
 * The feed's own row joins for `loadedFirstPage`, which flips after the thread's opening fetch and lifts the events'
 * floor, so `feed` is among the collections the thread recomputes on.
 *
 * The driver's TMS events always join: the thread is the Dispatch page's, and a workspace on that page has its events
 * loaded. Without a driver there is no membership to read them for. The driver's tasks join only with `viewLabelFor`,
 * which the page passes while the workspace shows tasks.
 */
export function threadSourcesFor(
  feedId: string,
  driver: ThreadDriver | null,
  language: string,
  now: number,
  viewLabelFor: ViewLabelFor,
) {
  const codec = targetCodecForBrowser();
  const items = [...rowsByIndex("item", idx.itemsByFeed, feedId)];
  const eventsByItem = new Map<string, AccountEvent[]>();
  for (const item of items) {
    eventsByItem.set(item.id, [...rowsByIndex("accountEvent", idx.accountEventsByItem, item.id)]);
  }
  const sources: ThreadSources = {
    items,
    driverAccountId: driver?.accountId ?? null,
    acknowledgements: {
      feed: feedFold(feedId),
      itemFold: feedItemFold,
      eventFold: (event) => (isAcknowledgeableTmsEvent(event) ? tmsEventFold(event) : null),
    },
    actorNameFor: nameForMembership,
    tmsEvents: driver ? rowsByIndex("tmsEvent", idx.tmsEventsByMembership, driver.workspaceMembershipId) : [],
    historyExhausted: store.get("feed", feedId)?.loadedFirstPage === true,
    eventsByItem,
    textFor: (contentId) => transcriptionForContent(contentId, language)?.transcriptionContent ?? null,
    audioSecondsFor: (contentId) => {
      const encodings = [...rowsByIndex("audioEncoding", idx.audioEncodingsByContent, contentId)];
      const playable = encodings.find((encoding) => encoding.codec === codec) ?? encodings[0];
      const seconds = playable ? Number(playable.duration) : Number.NaN;
      return Number.isFinite(seconds) ? seconds : null;
    },
    filesFor: (contentId) => [...rowsByIndex("file", idx.filesByContent, contentId)],
    linksFor: (contentId) => [...rowsByIndex("link", idx.linksByContent, contentId)],
    nameFor: nameForAccount,
    now,
    tasks: viewLabelFor && driver ? driverTasks(feedId, driver.workspaceMembershipId, now) : [],
    viewLabelFor: viewLabelFor ?? NO_VIEW_LABEL,
  };
  return sources;
}

/**
 * The thread a driver's History pane shows, recomputed as items, transcriptions, audio, events, accounts, TMS events,
 * and tasks land. `now` moves the acknowledgement lookback; hand it a minute clock. The driver's tasks join only with
 * `viewLabelFor`, which the page passes while the workspace shows tasks.
 */
export function useDispatchThread(
  feedId: string | null,
  driver: ThreadDriver | null,
  now: number,
  viewLabelFor: ViewLabelFor,
): ThreadEntry[] {
  const { myAccount } = useContext(MyAccountContext);
  const language = myAccount?.preferredLanguage ?? "en";
  const accountId = driver?.accountId ?? null;
  const membershipId = driver?.workspaceMembershipId ?? null;
  const computed = useCollectionsCompute(
    threadCollections,
    () => (feedId ? buildDispatchThread(threadSourcesFor(feedId, driver, language, now, viewLabelFor)) : null),
    [feedId, accountId, membershipId, language, now, viewLabelFor],
    "Dispatch:thread",
  );
  return useMemo(() => computed ?? NO_ENTRIES, [computed]);
}
